// ============================================================================
// AdminDocumentsPanel — compliance documents (Build S2, Task #18).
// ============================================================================
// Sidebar entry: #admin/documents. UIF only.
//
// Two modes (route-driven, S1 Farms precedent):
//   * LIST   (#admin/documents):      KPI row, search/type/status/farm filters,
//                                     flat table, upload modal.
//   * DETAIL (#admin/documents/:id):  metadata + status controls, versions
//                                     (upload new), approvals (request/act),
//                                     shares (share to farm, ack timestamps).
//
// Upload flow (contract §14.2.6 — verified checksums): the browser computes
// sha256 via WebCrypto, the API presigns a PUT that BINDS that checksum
// (x-amz-checksum-sha256 is a signed param — S3 verifies server-side), the
// browser PUTs directly to S3, then finalize re-verifies via HeadObject
// before the version becomes visible anywhere.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, Plus, AlertCircle, FileText, Download, Share2, Archive, ArrowLeft,
  ShieldAlert, Users, X as XIcon, UploadCloud, Edit2,
} from 'lucide-react';
import {
  api,
  type AdminDocument,
  type AdminDocumentDetail,
  type DocumentKpis,
  type DocumentTypeInfo,
  type DocumentScopeType,
  type DocumentStatus,
  type AdminFarm,
} from '../api';
import { sha256Base64, documentMime, putToS3 } from '../lib/documentUpload';

interface Props {
  token: string;
  /** When set, the panel renders the detail view for that document. */
  documentId?: string;
}

const STATUS_OPTIONS: DocumentStatus[] = ['draft', 'in_review', 'approved', 'final', 'superseded', 'void'];

export function AdminDocumentsPanel({ token, documentId }: Props) {
  if (documentId !== undefined) return <DocumentDetail token={token} documentId={documentId} />;
  return <DocumentList token={token} />;
}

// ----------------------------------------------------------------------------
// Upload plumbing (sha256Base64 / documentMime / putToS3) now lives in the
// shared module ../lib/documentUpload so S6b's record-external panels reuse the
// exact signed-checksum flow. Imported above.
// ----------------------------------------------------------------------------

function formatBytes(size: string | number | null): string {
  const n = Number(size ?? 0);
  if (!n) return '—';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================================================
// LIST MODE
// ============================================================================
function DocumentList({ token }: { token: string }) {
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [kpis, setKpis] = useState<DocumentKpis | null>(null);
  const [types, setTypes] = useState<DocumentTypeInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // S3 chore-ledger item 3: filter the catalog by farm.
  const [farmFilter, setFarmFilter] = useState('');
  const [farms, setFarms] = useState<AdminFarm[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    api.adminFarms(token, { include_test: true }).then((r) => setFarms(r.farms)).catch(() => undefined);
  }, [token]);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.adminDocuments(token, {
        q: search.trim() || undefined,
        type_code: typeFilter || undefined,
        status: statusFilter || undefined,
        farm_id: farmFilter ? Number(farmFilter) : undefined,
      });
      setDocuments(res.documents);
      setKpis(res.kpis);
      setTypes(res.types);
    } catch (e: any) {
      setError(e?.message || 'Could not load documents');
    } finally {
      setLoading(false);
    }
  }, [token, search, typeFilter, statusFilter, farmFilter]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-uif-green-dark flex items-center gap-2">
            <FileText className="h-5 w-5" /> Documents
          </h1>
          <p className="text-sm text-gray-500">
            Compliance documents — S3-backed, versioned, shareable to producer farms.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
        >
          <UploadCloud className="h-4 w-4" />
          Upload document
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Documents" value={kpis?.total_documents ?? 0} tone="ok" icon={<FileText className="h-4 w-4" />} />
        <Kpi label="Pending approval" value={kpis?.pending_approval ?? 0} tone="warn" icon={<AlertCircle className="h-4 w-4" />} />
        <Kpi label="Shared to producers" value={kpis?.shared_documents ?? 0} tone="amp" icon={<Share2 className="h-4 w-4" />} />
        <Kpi label="Restricted" value={kpis?.restricted_documents ?? 0} tone="warn" icon={<ShieldAlert className="h-4 w-4" />} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <label className="block flex-1 min-w-[200px]">
          <span className="block text-xs font-medium text-gray-700 mb-1">Search</span>
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Title"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Type</span>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
            <option value="">All</option>
            {types.map((t) => <option key={t.type_code} value={t.type_code}>{t.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Farm</span>
          <select value={farmFilter} onChange={(e) => setFarmFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
            <option value="">All farms</option>
            {farms.map((f) => <option key={f.farm_id} value={f.farm_id}>{f.farm_code} — {f.farm_name}{f.is_test ? ' [TEST]' : ''}</option>)}
          </select>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Farm</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Versions</th>
              <th className="px-3 py-2 text-right">Shares</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && documents.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin inline" /> Loading…
              </td></tr>
            )}
            {!loading && documents.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No documents yet — upload the first one.</td></tr>
            )}
            {documents.map((d) => (
              <tr
                key={d.document_id}
                role="link"
                tabIndex={0}
                aria-label={`Open document ${d.title}`}
                className="hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-uif-green focus:ring-inset"
                onClick={() => { window.location.hash = `#admin/documents/${d.document_id}`; }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.location.hash = `#admin/documents/${d.document_id}`;
                  }
                }}
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{d.title}</div>
                  <div className="text-xs text-gray-500">{d.file_name ?? 'no finalized version'}</div>
                </td>
                <td className="px-3 py-2 text-gray-700">{d.type_label}</td>
                <td className="px-3 py-2 font-mono text-gray-700">{d.farm_code ?? '—'}</td>
                <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                <td className="px-3 py-2 text-right text-gray-700">{d.version_count}</td>
                <td className="px-3 py-2 text-right text-gray-700">{d.share_count}</td>
                <td className="px-3 py-2 text-gray-500 text-xs">{new Date(d.updated_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-right">
                  <a
                    href={`#admin/documents/${d.document_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-uif-green-dark hover:underline"
                  >
                    Open →
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {uploadOpen && (
        <UploadModal
          token={token}
          types={types}
          onClose={() => setUploadOpen(false)}
          onDone={(docId) => { setUploadOpen(false); window.location.hash = `#admin/documents/${docId}`; }}
        />
      )}
    </div>
  );
}

// ============================================================================
// DETAIL MODE
// ============================================================================
function DocumentDetail({ token, documentId }: { token: string; documentId: string }) {
  const [detail, setDetail] = useState<AdminDocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [shareFarms, setShareFarms] = useState<AdminFarm[]>([]);
  const [shareFarmId, setShareFarmId] = useState<number | ''>('');
  const versionFileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setDetail(await api.adminDocument(token, documentId));
    } catch (e: any) {
      setError(e?.message || 'Could not load document');
    } finally {
      setLoading(false);
    }
  }, [token, documentId]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    api.adminFarms(token, { include_test: true }).then((r) => setShareFarms(r.farms)).catch(() => undefined);
  }, [token]);

  async function setStatus(status: DocumentStatus) {
    setBusy(true); setError('');
    try { await api.updateDocument(token, documentId, { status }); await refresh(); }
    catch (e: any) { setError(e?.message || 'Update failed'); }
    finally { setBusy(false); }
  }

  async function download(versionId?: string) {
    try {
      const r = await api.adminDocumentDownloadUrl(token, documentId, versionId);
      window.location.href = r.download_url;
    } catch (e: any) { setError(e?.message || 'Download failed'); }
  }

  async function uploadNewVersion(file: File) {
    setBusy(true); setError('');
    try {
      const hash = await sha256Base64(file);
      const mime = documentMime(file);
      const t = await api.documentVersionUploadUrl(token, documentId, {
        file_name: file.name, mime_type: mime, byte_size: file.size, sha256_base64: hash,
      });
      await putToS3(t.upload_url, file, mime, hash);
      await api.documentFinalize(token, documentId, t.version_id);
      await refresh();
    } catch (e: any) { setError(e?.message || 'Version upload failed'); }
    finally { setBusy(false); }
  }

  async function share() {
    if (shareFarmId === '') return;
    setBusy(true); setError('');
    try { await api.shareDocument(token, documentId, Number(shareFarmId)); setShareFarmId(''); await refresh(); }
    catch (e: any) { setError(e?.message || 'Share failed'); }
    finally { setBusy(false); }
  }

  async function unshare(shareId: string) {
    if (!window.confirm('Remove this share? Producers on that farm will no longer see the document.')) return;
    setBusy(true); setError('');
    try { await api.unshareDocument(token, documentId, shareId); await refresh(); }
    catch (e: any) { setError(e?.message || 'Unshare failed'); }
    finally { setBusy(false); }
  }

  async function actApproval(approvalId: string, status: 'approved' | 'changes_requested') {
    setBusy(true); setError('');
    try { await api.actOnDocumentApproval(token, approvalId, { status }); await refresh(); }
    catch (e: any) { setError(e?.message || 'Approval action failed'); }
    finally { setBusy(false); }
  }

  async function archiveDoc() {
    if (!detail) return;
    if (!window.confirm(`Archive "${detail.document.title}"? It disappears from lists but nothing is deleted.`)) return;
    try { await api.archiveDocument(token, documentId); window.location.hash = '#admin/documents'; }
    catch (e: any) { setError(e?.message || 'Archive failed'); }
  }

  if (loading && !detail) {
    return <div className="py-16 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin inline" /> Loading document…</div>;
  }
  if (!detail) {
    return (
      <div className="space-y-3">
        <BackLink />
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error || 'Document not found.'}</div>
      </div>
    );
  }

  const { document: doc, versions, approvals, shares } = detail;
  const shareable = ['approved', 'final'].includes(doc.status) && doc.current_version_id;
  const sharedFarmIds = new Set(shares.map((s) => s.farm_id));

  return (
    <div className="space-y-4">
      <BackLink />

      {/* Header + status controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900">{doc.title}</h1>
              <StatusPill status={doc.status} />
              {doc.sensitivity_level === 'restricted' && <Pill tone="warn">Restricted</Pill>}
              {doc.visibility === 'producer_shared' && <Pill tone="amp">Producer shared</Pill>}
              {doc.is_archived && <Pill tone="warn">Archived</Pill>}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              {doc.type_label} · lane {doc.lane}{doc.farm_code ? <> · farm <span className="font-mono">{doc.farm_code}</span></> : null}
            </div>
            {doc.retention_note && <div className="text-xs text-gray-400 mt-1">{doc.retention_note}</div>}
          </div>
          <div className="flex items-center gap-2">
            {doc.current_version_id && (
              <button type="button" onClick={() => void download()}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" /> Download
              </button>
            )}
            {!doc.is_archived && (
              <button type="button" onClick={() => void archiveDoc()}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50">
                <Archive className="h-4 w-4" /> Archive
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-gray-500">Set status:</span>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              disabled={busy || s === doc.status}
              onClick={() => void setStatus(s)}
              className={
                'text-xs px-2 py-1 rounded-md border ' +
                (s === doc.status
                  ? 'bg-uif-green/10 border-uif-green/40 text-uif-green-dark font-semibold'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50')
              }
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}

      {/* Versions */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <FileText className="h-4 w-4" /> Versions
          </h2>
          <div>
            <input
              ref={versionFileRef} type="file" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadNewVersion(f); e.target.value = ''; }}
            />
            <button type="button" disabled={busy} onClick={() => versionFileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-xs hover:bg-uif-green-dark disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Upload new version
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">File</th>
              <th className="px-3 py-2 text-right">Size</th>
              <th className="px-3 py-2">Uploaded</th>
              <th className="px-3 py-2">sha256</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {versions.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No finalized versions.</td></tr>
            )}
            {versions.map((v) => (
              <tr key={v.version_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-gray-900">
                  v{v.version_no}
                  {doc.current_version_id === v.version_id && (
                    <span className="ml-1.5"><Pill tone="ok">current</Pill></span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-700">{v.file_name}</td>
                <td className="px-3 py-2 text-right text-gray-700">{formatBytes(v.byte_size)}</td>
                <td className="px-3 py-2 text-gray-500 text-xs">
                  {new Date(v.uploaded_at).toLocaleString()}{v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''}
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-gray-400" title={v.sha256}>{v.sha256.slice(0, 12)}…</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => void download(v.version_id)}
                    className="text-gray-500 hover:text-uif-green-dark p-1" title="Download this version">
                    <Download className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Shares */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
          <Share2 className="h-4 w-4" /> Shared with farms
        </h2>
        {!shareable && (
          <p className="text-xs text-amber-700 mt-1">
            Sharing unlocks when the document has a finalized version and status approved or final.
          </p>
        )}
        {shareable && (
          <div className="mt-2 flex items-end gap-2">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Farm</span>
              <select value={shareFarmId} onChange={(e) => setShareFarmId(e.target.value === '' ? '' : Number(e.target.value))}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
                <option value="">Select…</option>
                {shareFarms.filter((f) => !sharedFarmIds.has(f.farm_id)).map((f) => (
                  <option key={f.farm_id} value={f.farm_id}>{f.farm_code} — {f.farm_name}{f.is_test ? ' [TEST]' : ''}</option>
                ))}
              </select>
            </label>
            <button type="button" disabled={busy || shareFarmId === ''} onClick={() => void share()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-xs hover:bg-uif-green-dark disabled:opacity-60">
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </div>
        )}
        <ul className="mt-3 space-y-1.5">
          {shares.length === 0 && <li className="text-sm text-gray-400">Not shared with any farm.</li>}
          {shares.map((s) => (
            <li key={s.share_id} className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
              <span className="font-mono font-semibold text-uif-green-dark">{s.farm_code}</span>
              <span>{s.farm_name}</span>
              <span className="text-xs text-gray-400">shared {new Date(s.shared_at).toLocaleDateString()}</span>
              {s.acknowledged_at
                ? <Pill tone="ok">viewed {new Date(s.acknowledged_at).toLocaleDateString()}</Pill>
                : <Pill tone="warn">not yet viewed</Pill>}
              <button type="button" onClick={() => void unshare(s.share_id)}
                className="text-gray-400 hover:text-amber-700 p-0.5" title="Remove share">
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Approvals */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
          <Users className="h-4 w-4" /> Approvals
        </h2>
        <ul className="mt-2 space-y-1.5">
          {approvals.length === 0 && <li className="text-sm text-gray-400">No approval requests.</li>}
          {approvals.map((a) => (
            <li key={a.approval_id} className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-gray-500">v{a.version_no}</span>
              <span>{a.role_label === 'PRODUCER' ? a.member_name : a.admin_name}</span>
              <span className="text-xs text-gray-400">({a.role_label.toLowerCase().replace('_', ' ')})</span>
              {a.status === 'requested' ? (
                <>
                  <Pill tone="warn">pending</Pill>
                  <button type="button" disabled={busy} onClick={() => void actApproval(a.approval_id, 'approved')}
                    className="text-xs text-uif-green-dark hover:underline">approve</button>
                  <button type="button" disabled={busy} onClick={() => void actApproval(a.approval_id, 'changes_requested')}
                    className="text-xs text-amber-700 hover:underline">request changes</button>
                </>
              ) : (
                <Pill tone={a.status === 'approved' ? 'ok' : 'warn'}>{a.status.replace('_', ' ')}</Pill>
              )}
              {a.comment && <span className="text-xs text-gray-500 italic">“{a.comment}”</span>}
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
          <Edit2 className="h-3 w-3" /> Approval requests are created from the API per finalized version; the panel lists and acts on them.
        </p>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <a href="#admin/documents" className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
      <ArrowLeft className="h-4 w-4" /> All documents
    </a>
  );
}

// ============================================================================
// Upload modal (drag-drop + type/scope picker → presign → PUT → finalize)
// ============================================================================
function UploadModal({
  token, types, onClose, onDone,
}: {
  token: string;
  types: DocumentTypeInfo[];
  onClose: () => void;
  onDone: (documentId: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [typeCode, setTypeCode] = useState('');
  const [scopeType, setScopeType] = useState<DocumentScopeType>('none');
  const [farmId, setFarmId] = useState<number | ''>('');
  const [farms, setFarms] = useState<AdminFarm[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.adminFarms(token, { include_test: true }).then((r) => setFarms(r.farms)).catch(() => undefined);
  }, [token]);

  function acceptFile(f: File) {
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  }

  async function upload(e: React.FormEvent) {
    e.preventDefault();
    if (!file || busy) return;
    if (!typeCode) { setError('Pick a document type'); return; }
    if (scopeType === 'farm' && farmId === '') { setError('Pick the farm this document belongs to'); return; }
    setBusy(true); setError('');
    try {
      setPhase('Computing checksum…');
      const hash = await sha256Base64(file);
      const mime = documentMime(file);
      setPhase('Requesting upload URL…');
      const t = await api.documentUploadUrl(token, {
        title: title.trim(),
        type_code: typeCode,
        scope_type: scopeType,
        scope_id: scopeType === 'farm' ? String(farmId) : undefined,
        farm_id: scopeType === 'farm' ? Number(farmId) : undefined,
        file_name: file.name,
        mime_type: mime,
        byte_size: file.size,
        sha256_base64: hash,
      });
      setPhase('Uploading to S3…');
      await putToS3(t.upload_url, file, mime, hash);
      setPhase('Verifying…');
      await api.documentFinalize(token, t.document_id, t.version_id);
      onDone(t.document_id);
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
      setPhase('');
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Upload document" onClose={onClose}>
      <form onSubmit={upload} className="space-y-3">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) acceptFile(f);
          }}
          onClick={() => fileRef.current?.click()}
          className={
            'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ' +
            (dragOver ? 'border-uif-green bg-uif-green/5' : 'border-gray-300 hover:border-uif-green/60')
          }
        >
          <input
            ref={fileRef} type="file" className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.xlsx,.docx,.pptx,.md,.csv"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) acceptFile(f); e.target.value = ''; }}
          />
          <UploadCloud className="h-6 w-6 mx-auto text-gray-400" />
          {file ? (
            <div className="mt-1 text-sm text-gray-900 font-medium">{file.name} <span className="text-gray-500">({formatBytes(file.size)})</span></div>
          ) : (
            <div className="mt-1 text-sm text-gray-500">Drag a file here or click to browse (PDF, images, Office, CSV — max 100 MB)</div>
          )}
        </div>

        <Field label="Title *">
          <input required value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Type *">
            <select required value={typeCode} onChange={(e) => setTypeCode(e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {types.map((t) => <option key={t.type_code} value={t.type_code}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Scope">
            <select value={scopeType} onChange={(e) => setScopeType(e.target.value as DocumentScopeType)} className={inputCls}>
              <option value="none">No scope</option>
              <option value="farm">Farm</option>
            </select>
          </Field>
          {scopeType === 'farm' && (
            <Field label="Farm *">
              <select required value={farmId} onChange={(e) => setFarmId(e.target.value === '' ? '' : Number(e.target.value))} className={inputCls}>
                <option value="">Select…</option>
                {farms.map((f) => <option key={f.farm_id} value={f.farm_id}>{f.farm_code} — {f.farm_name}{f.is_test ? ' [TEST]' : ''}</option>)}
              </select>
            </Field>
          )}
        </div>

        {phase && (
          <div className="text-sm text-uif-green-dark flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> {phase}
          </div>
        )}
        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !file}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Shared bits (house styles per AdminFarmsPanel)
// ============================================================================
const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function StatusPill({ status }: { status: DocumentStatus }) {
  const tone = status === 'approved' || status === 'final' ? 'ok' : status === 'void' || status === 'superseded' ? 'warn' : 'amp';
  return <Pill tone={tone}>{status.replace('_', ' ')}</Pill>;
}

function Kpi({ label, value, tone, icon }: { label: string; value: number; tone: 'ok' | 'warn' | 'amp'; icon: React.ReactNode }) {
  const cls =
    tone === 'ok'   ? 'border-emerald-100 bg-emerald-50/50'
    : tone === 'amp' ? 'border-uif-green/20 bg-uif-green/5'
    : 'border-amber-100 bg-amber-50/50';
  return (
    <div className={'rounded-lg border p-3 ' + cls}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</div>
    </div>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'amp'; children: React.ReactNode }) {
  const cls =
    tone === 'ok'   ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : tone === 'amp' ? 'bg-uif-green/5 border-uif-green/30 text-uif-green-dark'
    : 'bg-amber-50 border-amber-200 text-amber-800';
  return <span className={'inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + cls}>{children}</span>;
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
